import { getPayDayRule, ordinal, type PayDayField, type PayDaySelection, type PayFrequencyKey } from './paySchedule';

interface Props {
  frequency: PayFrequencyKey;
  value: PayDaySelection;
  /** Omit for a read-only display (the selects are disabled). */
  onChange?: ((field: PayDayField, value: string) => void) | undefined;
  errors?: Partial<Record<PayDayField, string>> | undefined;
  /** Prefix for element ids when more than one set is on the page. */
  idPrefix?: string;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** A stored value outside the brief's options is still shown (never silently blanked), labelled as such. */
function legacyLabel(frequency: PayFrequencyKey, value: string): string {
  const n = Number(value);
  if (frequency === 'weekly' || frequency === 'biweekly') return `${WEEKDAYS[n] ?? value} (outside the standard options)`;
  return `${ordinal(n)} (outside the standard options)`;
}

/** The conditional Pay Day selector set for one pay frequency, plus the visible "Needs payroll confirmation" note where undefined. */
export function PayDaySelectors({ frequency, value, onChange, errors, idPrefix = 'payday' }: Props) {
  const rule = getPayDayRule(frequency);
  const readOnly = !onChange;
  return (
    <>
      {rule.controls.map((control) => {
        const current = value[control.field];
        const known = control.options.some((o) => o.value === current);
        const id = `${idPrefix}-${control.field}`;
        const error = errors?.[control.field];
        return (
          <label key={control.field} htmlFor={id}>
            {control.label}{readOnly ? '' : ' *'}
            <select
              id={id}
              value={current}
              disabled={readOnly}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${id}-error` : undefined}
              onChange={(e) => onChange?.(control.field, e.target.value)}
            >
              <option value="">{readOnly ? 'Set on the pay schedule' : `Select ${control.label.toLowerCase()}`}</option>
              {!known && current !== '' && <option value={current}>{legacyLabel(frequency, current)}</option>}
              {control.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {error && <span id={`${id}-error`} className="error" role="alert">{error}</span>}
          </label>
        );
      })}
      {rule.hint && (
        <p className="muted" data-testid="payday-hint" style={{ gridColumn: '1 / -1' }}>{rule.hint}</p>
      )}
      {rule.confirmationNote && (
        <p className="muted" data-testid="payday-confirmation-note" style={{ gridColumn: '1 / -1' }}>
          <strong>Needs payroll confirmation:</strong> {rule.confirmationNote}
        </p>
      )}
    </>
  );
}
